<template>
  <div
    :class="$b({ asLineLink })"
    :style="highlightStyles"
  >
    <div :class="$b('row-container')">
      <div :class="$b('row', { cols: tabs.length })">
        <component
          :is="asLineLink ? 'LineLink' : 'button'"
          v-for="(tab, idx) in tabs"
          :key="idx"
          :class="$b('tab', { active: activeTabIndex === idx })"
          :is-button="asLineLink"
          @click="$emit('selected', { idx: idx, value: tab.value })"
        >
          <BaseCopy
            tag="span"
            size="h4"
            :class="$b('tab-text')"
          >
            {{ tab.name }}
          </BaseCopy>
        </component>
      </div>
    </div>
  </div>
</template>

<script>
import { LineLink } from '#components'

export default {
  name: 'RadioTabs',

  components: {
    LineLink
  },

  props: {
    tabs: { type: Array, required: true },
    activeTabIndex: { type: Number, required: true },
    asLineLink: { type: Boolean, default: false }
  },

  emits: ['selected', 'update:activeTab'],

  computed: {
    highlightStyles () {
      return `--highlight-width: ${100 / this.tabs.length}%;
        --highlight-pos: ${this.highlightPosition};`
    },
    highlightPosition () {
      return `calc(${this.activeTabIndex * 100}%)`
    }
  },
  watch: {
    tabs (newValue, oldValue) {
      if (newValue.length !== oldValue.length) this.$emit('update:activeTab', { idx: this.idx, value: this.tab.value })
    }
  }
}
</script>

<style lang="postcss">
.RadioTabs {
  @apply w-full z-0;
}

.RadioTabs__row-container {
  @apply relative
    bg-grey-light
    rounded-[4px]
    px-[4px]
    w-full z-0;

  .RadioTabs--as-line-link & {
    @apply bg-transparent;
  }
}

.RadioTabs__row {
  @apply relative
    inline-flex items-center justify-evenly flex-shrink
    w-full z-0;

  .RadioTabs--as-line-link & {
    @apply w-auto whitespace-nowrap inline-grid;

    &--cols-2 {
      @apply grid-cols-2;
    }

    &--cols-3 {
      @apply grid-cols-3;
    }

    &--cols-4 {
      @apply grid-cols-4;
    }

    &--cols-5 {
      @apply grid-cols-5;
    }
  }
  
  &::before {
    @apply absolute transition-transform left-0;
    content: '';
    background-image: var(--gradient-default);
    border-radius: 4px;
    height: calc(100% - theme('spacing.xxxs'));
    width: var(--highlight-width);
    transform: translate3d(var(--highlight-pos), 0, 0);
    transition-duration: 0.3s;
    z-index: 1;

    .RadioTabs--as-line-link & {
      @apply rounded-none;
      height: 2px;
      bottom: -5px;
      z-index: 5;
    }
  }
}

.RadioTabs__tab {
  @apply text-off-black text-center
    flex justify-center items-center;
  -webkit-tap-highlight-color: transparent;
  flex: 1 1 100%;
  appearance: none;
  z-index: 2;

  &:not(&--active) {
    @apply opacity-60;
    transition: colour 0.25s ease-in-out;
  }

  .RadioTabs:not(.RadioTabs--as-line-link) & {
    @apply py-xs;
  }
  
  .RadioTabs:not(.RadioTabs--as-line-link) &:last-of-type {
    @apply pr-0;
  }

  &:focus {
    outline: none;
  }

  &:hover:not(&--active) {
    @apply opacity-80;
  }

  &--active {
    .RadioTabs__tab-text {
      @apply text-white;

      .RadioTabs--as-line-link & {
        @apply text-off-black;
      }
    }
  }
}

.RadioTabs__tab-text {
  @apply uppercase;

  .RadioTabs--as-line-link & {
    @apply normal-case;
  }

  font-size: 11px;

  @screen xxs {
    @apply text-xs;
  }
}
</style>
